// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
import { formatNumber, FORMATTERS, LEGEND_FORMATTERS } from "./formatters.js";
import { setContext, getContext } from "svelte";

/**
 * @typedef {Object} Config
 */

let contextKey = Symbol("config");

export function getConfig() {
  return getContext(contextKey);
}

/** @param {any} config */
export function setConfig(config) {
  setContext(contextKey, config);
}

/**
 * @typedef {Object} ColumnConfig
 * @property {string} column_name
 * @property {string} column_label
 * @property {string} data_formatting
 * @property {string} scale_color
 * @property {string} scale_type
 * @property {string} scale_breaks
 * @property {string} scale_min_max
 * @property {boolean | string} show_on_map
 * @property {string} [table_label]
 * @property {(d: any) => string} [format]
 * @property {(d: any) => string} [legendFormat]
 */

/**
 * @param {ColumnConfig[]} columnConfig
 * @param {string} accentColor
 */
export function createConfig(columnConfig, accentColor) {
  let parsedConfig = parseConfig(columnConfig, accentColor);
  return parsedConfig;
}

const baseConfig = {
  map: {
    stroke: urbanColors.white,
    /** @type {string} */
    hoverStroke: urbanColors.yellow,
    strokeWidth: 1,
    hoverStrokeWidth: 5,
    fontColor: urbanColors.gray_shade_darkest,
    fontWeight: "bold",
    fontSize: 18
  }
};

/**
 * @param {ColumnConfig[]} columnConfig
 * @param {string} accentColor
 */
function parseConfig(columnConfig, accentColor) {
  let columnMap = new Map();
  for (let col of columnConfig) {
    columnMap.set(col.column_name, parseColumnConfig(col));
  }
  let combinedConfig = Object.assign(baseConfig, { columns: columnMap });
  if (accentColor) {
    combinedConfig.map.hoverStroke = accentColor;
  }
  return combinedConfig;
}

/**
 * @param {ColumnConfig} columnConfig
 */
function parseColumnConfig(columnConfig) {
  let result = {
    column_name: columnConfig.column_name,
    column_label: columnConfig.column_label.trim()
      ? columnConfig.column_label
      : columnConfig.column_name,
    data_formatting: columnConfig.data_formatting,
    scale_color: columnConfig.scale_color.trim() === "" ? "blue" : columnConfig.scale_color,
    scale_type: columnConfig.scale_type === "" ? "linear" : columnConfig.scale_type,
    scale_breaks:
      columnConfig.scale_breaks.trim() === ""
        ? undefined
        : parseArrayStr(columnConfig.scale_breaks),
    scale_min_max:
      columnConfig.scale_min_max.trim() === ""
        ? undefined
        : parseArrayStr(columnConfig.scale_min_max),
    show_on_map: columnConfig.show_on_map !== "false" && columnConfig.show_on_map !== false,
    /** @type {(d: any) => string} */
    format: (d) => String(d),
    /** @type {(d: any) => string} */
    legendFormat: (d) => String(d)
  };
  let defaultFormats = Object.keys(FORMATTERS);
  if (defaultFormats.includes(result.data_formatting)) {
    result.format = (/** @type {any} */ d) => formatNumber(d, /** @type {any} */ (result.data_formatting));
    result.legendFormat = (/** @type {any} */ d) => LEGEND_FORMATTERS[/** @type {any} */ (result.data_formatting)](d);
  } else {
    console.warn(`Unknown data_formatting: "${result.data_formatting}". Using plain string fallback.`);
    result.format = (/** @type {any} */ d) => String(d);
    result.legendFormat = (/** @type {any} */ d) => String(d);
  }
  return result;
}

/**
 * @param {string | undefined} arrayStr
 * @returns {number[] | undefined}
 */
function parseArrayStr(arrayStr) {
  return arrayStr ? arrayStr.split(",").map((str) => Number(str.trim())) : undefined;
}

/**
 * Translates config columns into generic Table props (formatters + labels).
 * @param {object} config - The parsed config object (with .columns Map)
 * @param {string[]} columnNames - Which columns to translate
 * @returns {{ formatters: Record<string, Function>, headers: Record<string, string> }}
 */
export function getTableProps(config, columnNames) {
  const formatters = {};
  const headers = {};
  for (const col of columnNames) {
    const colConfig = config.columns.get(col);
    if (colConfig) {
      if (colConfig.format) formatters[col] = colConfig.format;
      headers[col] = colConfig.table_label || colConfig.column_label || col;
    }
  }
  return { formatters, headers };
}
